
import type { Cast } from '@/hooks/useCasts';

// Utility function to inject ads and announcements into feed items
export const injectAdsIntoFeed = (
  feedItems: any[], 
  casts: Cast[], 
  announcements: any[] = [],
  externalAdInterval: number = 10, 
  isoCastInterval: number = 3
) => {
  const feedWithAds: any[] = [];
  let externalAdCount = 0;
  let isoCastAdCount = 0;

  // Filter out expired ISO Casts
  const availableCasts = casts.filter(cast => {
    if (cast.solved) return false;
    
    // Check if the cast has expired based on ad_placement_expires_at
    if (cast.ad_placement_expires_at) {
      const expirationDate = new Date(cast.ad_placement_expires_at);
      const now = new Date();
      return now < expirationDate;
    }
    
    // If no expiration date is set, keep the cast (backward compatibility)
    return true;
  });

  // Filter active announcements
  const activeAnnouncements = announcements.filter(announcement => {
    if (!announcement.is_active) return false;
    
    const now = new Date();
    
    // Check start date
    if (announcement.start_date) {
      const startDate = new Date(announcement.start_date);
      if (now < startDate) return false;
    }
    
    // Check end date
    if (announcement.end_date) {
      const endDate = new Date(announcement.end_date);
      if (now > endDate) return false;
    }
    
    return true;
  });

  // Add sticky announcements at the top
  const stickyAnnouncements = activeAnnouncements.filter(a => a.display_type === 'sticky');
  stickyAnnouncements.forEach((announcement, index) => {
    feedWithAds.push({
      id: `sticky-announcement-${announcement.id}`,
      type: 'announcement',
      announcement,
      timestamp: new Date().toISOString(),
      _isAnnouncement: true,
      _isSticky: true
    });
  });

  // Get frequency-based announcements
  const frequencyAnnouncements = activeAnnouncements.filter(a => a.display_type === 'frequency');
  let announcementIndex = 0;

  feedItems.forEach((item, index) => {
    // Add the regular feed item
    feedWithAds.push(item);

    // Check if we should add a frequency-based announcement
    if (frequencyAnnouncements.length > 0) {
      const announcement = frequencyAnnouncements[announcementIndex % frequencyAnnouncements.length];
      const frequency = announcement.post_frequency || 10;
      
      if ((index + 1) % frequency === 0 && index < feedItems.length - 1) {
        feedWithAds.push({
          id: `freq-announcement-${announcement.id}-${Math.floor(index / frequency)}`,
          type: 'announcement',
          announcement,
          timestamp: item.timestamp || item.created_at || new Date().toISOString(),
          _isAnnouncement: true,
          _isFrequency: true
        });
        announcementIndex++;
      }
    }

    // Check if we should add an ISO Cast ad after this item (every 3 posts)
    if ((index + 1) % isoCastInterval === 0 && index < feedItems.length - 1) {
      // Get a random ISO Cast that hasn't been solved and hasn't expired
      if (availableCasts.length > 0) {
        const randomCast = availableCasts[isoCastAdCount % availableCasts.length];
        feedWithAds.push({
          id: `iso-cast-ad-${isoCastAdCount}`,
          type: 'iso-cast-ad',
          adIndex: isoCastAdCount,
          cast: randomCast,
          timestamp: item.timestamp || item.created_at,
          _isAd: true
        });
        isoCastAdCount++;
      }
    }
    // Check if we should add an external ad after this item (every 10 posts)
    else if ((index + 1) % externalAdInterval === 0 && index < feedItems.length - 1) {
      feedWithAds.push({
        id: `external-ad-${externalAdCount}`,
        type: 'external-ad',
        adIndex: externalAdCount,
        timestamp: item.timestamp || item.created_at,
        _isAd: true
      });
      externalAdCount++;
    }
  });

  return feedWithAds;
};

// Helper function to check if an item is an ad
export const isAdItem = (item: any): boolean => {
  return item.type === 'external-ad' || item.type === 'iso-cast-ad' || item._isAd === true;
};

// Helper function to check if an item is an announcement
export const isAnnouncementItem = (item: any): boolean => {
  return item.type === 'announcement' || item._isAnnouncement === true;
};

// Helper function to check if an item is an ISO Cast ad
export const isIsoCastAd = (item: any): boolean => {
  return item.type === 'iso-cast-ad';
};

// Helper function to check if an item is an external ad
export const isExternalAd = (item: any): boolean => {
  return item.type === 'external-ad';
};
